import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useCallback, useEffect } from "react";

import { Box, Button, Grid, Text } from "~/components/blocks";
import { PharmacyAppShell } from "~/components/layouts";
import { useFetchPharmacy } from "~/components/pages/Pharmacy/use-fetch-pharmacy";
import { PharmacyProfile } from "~/components/partials";
import { AppDownloadBanner } from "~/components/partials/AppDownloadBanner";
import { useScrollToTop } from "~/hooks/use-scroll-to-top";

import { DEFAULT_CENTER, Map } from "./Map";
import { NextFlow } from "./NextFlow";
import { Questions } from "./Questions";

// https://developers.google.com/maps/documentation/urls/get-started#search-action
const GOOGLE_MAP_SEARCH_URL =
  "https://www.google.com/maps/search/?api=1&query=";

type Props = {
  onBack: () => void;
  pharmacyId?: string;
};

const Title = styled("div")(({ theme }) =>
  css({
    background: theme.colors.background.gradation,
    color: theme.colors.text.white,
    fontWeight: theme.fontWeights.bold,
    padding: theme.space.l,
  }),
);

const Wrap = styled(Box)(({ theme }) =>
  css({
    width: "100%",
    maxWidth: theme.windowSize.base,
    margin: "0 auto",
    padding: "0",
    boxSizing: "border-box",
  }),
);

const buildLaunchMapUrl = (lat: number, long: number): string => {
  return `${GOOGLE_MAP_SEARCH_URL}${lat},${long}`;
};

export const Completed = React.memo((props: Props) => {
  const theme = useTheme();

  const [containerRef, scrollTop] = useScrollToTop<HTMLDivElement>();
  const { pharmacy } = useFetchPharmacy(props.pharmacyId);
  const lat =
    pharmacy?.organizationAddresses[0].address.latitude || DEFAULT_CENTER.lat;
  const long =
    pharmacy?.organizationAddresses[0].address.longitude || DEFAULT_CENTER.lng;

  const handleClick = useCallback(() => {
    const launchURL = buildLaunchMapUrl(lat, long);
    window.open(launchURL, "resizable,scrollbars,status,noopener");
  }, [lat, long]);

  useEffect(() => {
    scrollTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PharmacyAppShell
      ref={containerRef}
      title="送信完了"
      backText="最初に戻る"
      terms={false}
      policy={false}
      onBack={props.onBack}
    >
      <Grid gridTemplateRows="min-content 1fr" overflow="auto">
        <Title>
          <Wrap>処方箋の送信が完了しました</Wrap>
        </Title>
        <Box marginX={theme.space.l}>
          <Wrap>
            <Text block fontWeight="bold" marginTop={theme.space.l}>
              申し込み情報の控えを電話番号宛に送信しました
            </Text>
            <Text block size="m" marginTop={theme.space.l}>
              {pharmacy?.webBookingsSetting?.electronicPrescription
                ? "お薬の準備ができましたら、スマホにお知らせ通知が届きます。通知が届いたら、処方箋の原本または処方内容（控え）をお持ちのうえ、お気をつけてお越しください"
                : "お薬の準備ができましたら、スマホにお知らせ通知が届きます。通知が届いたら、処方箋の原本をお持ちのうえ、お気をつけてお越しください"}
            </Text>
            <PharmacyProfile outline withContact pharmacy={pharmacy} />
            <Box width="100%" height="300px">
              <Map lat={lat} long={long} />
            </Box>
            <Button
              size="l"
              wide="fill"
              marginTop={theme.space.l}
              onClick={handleClick}
            >
              地図アプリで確認する
            </Button>
          </Wrap>
        </Box>
        <Box marginX={theme.space.l} marginTop={theme.space.xl}>
          <Wrap>
            <NextFlow />
          </Wrap>
        </Box>
        {pharmacy != null && (
          <Box marginX={theme.space.l} marginTop={theme.space.xl}>
            <Wrap>
              <Box maxWidth="420px" marginX="auto">
                <AppDownloadBanner page="completed" pharmacy={pharmacy} />
              </Box>
            </Wrap>
          </Box>
        )}
        <Box marginX={theme.space.l} marginTop={theme.space.xl}>
          <Wrap>
            <Questions />
          </Wrap>
        </Box>
      </Grid>
    </PharmacyAppShell>
  );
});

Completed.displayName = "Completed";
